import { marked } from "marked";
import DOMPurify from "dompurify";
import i18n from "../i18n";
import { parseBpmnElements, renderIflowSvgString } from "./bpmnShared";
import { documentationSections, documentationExtensionFacts, orderedDocumentationExtensions } from "./iflowDocumentationPresentation";

// Fixed document palette, shared with the existing editorial print design.
const PAPER = { ink: "#0A3D4A", text: "#26343A", muted: "#73828A", line: "#E4EAEC", accent: "#E85D2E" };
const FONT = "Source Sans 3";
const PAGE_WIDTH = 475;
const translation = language => i18n.getFixedT(language === "en" ? "en" : "de", "iflowDocumentationPdf");
const plainHtml = value => {
  const el = new DOMParser().parseFromString(DOMPurify.sanitize(String(value)), "text/html");
  return el.body.textContent || "";
};
const decodeText = value => {
  // A bare fragment puts its leading whitespace before the implicit HTML body.
  // Keep an explicit text container so separate bold/code runs retain word spaces.
  const encoded = String(value).replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return new DOMParser().parseFromString(`<body><span>${encoded}</span></body>`, "text/html").body.textContent || "";
};

export function cleanDocumentationSvg(value) {
  const clean = DOMPurify.sanitize(String(value || ""), { USE_PROFILES: { svg: true, svgFilters: false },
    FORBID_TAGS: ["style", "image", "foreignObject", "script", "a", "animate", "set"] });
  const doc = new DOMParser().parseFromString(clean, "image/svg+xml");
  if (doc.querySelector("parsererror") || doc.documentElement.localName !== "svg") return null;
  for (const el of doc.querySelectorAll("*")) {
    for (const attr of Array.from(el.attributes)) {
      if (/href$/i.test(attr.name) && !/^#[\w-]+$/.test(attr.value)) el.removeAttribute(attr.name);
      if (/url\s*\(/i.test(attr.value) && !/^url\(#[\w-]+\)$/.test(attr.value)) el.removeAttribute(attr.name);
      if (attr.name === "style") el.removeAttribute(attr.name);
    }
    if (el.hasAttribute("font-family")) el.setAttribute("font-family", FONT);
  }
  const svg = doc.documentElement;
  svg.setAttribute("font-family", FONT);
  const box = (svg.getAttribute("viewBox") || "").trim().split(/[ ,]+/).map(Number);
  const width = box.length === 4 ? box[2] : parseFloat(svg.getAttribute("width"));
  const height = box.length === 4 ? box[3] : parseFloat(svg.getAttribute("height"));
  if (![width, height].every(n => Number.isFinite(n) && n > 0 && n < 100000)) return null;
  svg.setAttribute("width", String(width)); svg.setAttribute("height", String(height));
  return new XMLSerializer().serializeToString(svg);
}

export function documentationDiffMarks(beforeXml, afterXml) {
  const before = parseBpmnElements(beforeXml || ""), after = parseBpmnElements(afterXml || "");
  // Sequence flow endpoints and conditions are relevant even when no step changes.
  for (const [xml, map] of [[beforeXml, before], [afterXml, after]]) {
    const doc = new DOMParser().parseFromString(xml || "", "application/xml");
    for (const el of doc.getElementsByTagNameNS("*", "sequenceFlow")) {
      const id = el.getAttribute("id");
      if (id) map[id] = { name: el.getAttribute("name"), source: el.getAttribute("sourceRef"), target: el.getAttribute("targetRef"), condition: el.textContent.trim() };
    }
    for (const el of doc.getElementsByTagNameNS("*", "messageFlow")) {
      const entry = map[el.getAttribute("id")];
      if (entry) Object.assign(entry, { source: el.getAttribute("sourceRef"), target: el.getAttribute("targetRef") });
    }
  }
  const stable = value => JSON.stringify(value, (key, val) => val && typeof val === "object" && !Array.isArray(val)
    ? Object.fromEntries(Object.entries(val).sort(([a], [b]) => a.localeCompare(b))) : val);
  const marksBefore = {}, marksAfter = {};
  for (const id of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (!before[id]) marksAfter[id] = "add";
    else if (!after[id]) marksBefore[id] = "del";
    else if (stable(before[id]) !== stable(after[id])) { marksBefore[id] = "chg"; marksAfter[id] = "chg"; }
  }
  return { marksBefore, marksAfter };
}

function inline(tokens = []) {
  return tokens.flatMap(token => {
    const children = token.tokens ? inline(token.tokens) : [{ text: token.type === "html"
      ? plainHtml(token.text || token.raw || "") : token.type === "codespan" ? token.text : decodeText(token.text || token.raw || "") }];
    if (token.type === "br") return [{ text: "\n" }];
    if (token.type === "image") return [{ text: token.text || "" }];
    return children.map(child => ({ ...child,
      ...(token.type === "strong" ? { bold: true } : {}),
      ...(token.type === "em" ? { italics: true } : {}),
      ...(token.type === "del" ? { decoration: "lineThrough" } : {}),
      ...(token.type === "codespan" ? { color: PAPER.ink } : {}),
      ...(token.type === "link" && /^https?:\/\//i.test(token.href) ? { link: token.href, decoration: "underline" } : {}),
    }));
  });
}

function diagramNode(svg, caption, t, width = PAGE_WIDTH, height = 520) {
  const safe = cleanDocumentationSvg(svg);
  return { stack: [
    ...(caption ? [{ text: caption, fontSize: 10, bold: true, color: PAPER.muted, margin: [0, 8, 0, 6] }] : []),
    safe ? { svg: safe, fit: [width, height] } : { text: t("diagramUnavailable"), color: PAPER.muted },
  ], margin: [0, 6, 0, 14], unbreakable: true };
}

function markdownNodes(markdown, diagrams, t) {
  const used = new Set();
  const marker = /\[\[\s*DIAGRAMM\s*:\s*([^\]|]+)(?:\|([^\]]*))?\]\]/gi;
  const normal = value => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const renderTokens = tokens => tokens.flatMap(token => {
    if (token.type === "space" || token.type === "def") return [];
    if (token.type === "heading") return [{ text: inline(token.tokens), style: `h${Math.min(token.depth, 4)}`, headlineLevel: token.depth }];
    if (token.type === "list") return [{ [token.ordered ? "ol" : "ul"]: token.items.map(item => ({ stack: renderTokens(item.tokens) })),
      ...(token.ordered && Number.isInteger(token.start) ? { start: token.start } : {}), margin: [0, 4, 0, 8] }];
    if (token.type === "blockquote") return [{ stack: renderTokens(token.tokens), margin: [12, 4, 0, 8], color: PAPER.muted }];
    if (token.type === "table") {
      const cell = c => ({ text: inline(c.tokens), margin: [4, 4, 4, 4] });
      return [{ table: { headerRows: 1, widths: token.header.map(() => "*"), body: [
        token.header.map(c => ({ ...cell(c), bold: true, color: PAPER.ink })), ...token.rows.map(row => row.map(cell)),
      ] }, layout: "lightHorizontalLines", fontSize: 9, margin: [0, 8, 0, 12] }];
    }
    if (token.type === "code") return token.lang === "svg"
      ? [diagramNode(token.text, "", t)] : [{ text: token.text, font: FONT, fontSize: 9, margin: [8, 6, 0, 10] }];
    if (token.type === "hr") return [{ canvas: [{ type: "line", x1: 0, y1: 0, x2: PAGE_WIDTH, y2: 0, lineWidth: 0.5, lineColor: PAPER.line }], margin: [0, 8, 0, 8] }];
    return [{ text: token.tokens ? inline(token.tokens) : plainHtml(token.text || token.raw || ""), margin: [0, 0, 0, 8] }];
  });
  const nodes = []; let cursor = 0, match;
  while ((match = marker.exec(String(markdown)))) {
    nodes.push(...renderTokens(marked.lexer(String(markdown).slice(cursor, match.index))));
    const id = normal(match[1].replace(/^bpmn\s*:/i, ""));
    const index = diagrams.findIndex((d, n) => !used.has(n) && normal(d.id || d.name) === id);
    if (index >= 0) { used.add(index); nodes.push(diagramNode(diagrams[index].svg, match[2]?.trim() || diagrams[index].name, t)); }
    else nodes.push({ text: t("diagramUnavailable"), color: PAPER.muted, margin: [0, 6, 0, 10] });
    cursor = marker.lastIndex;
  }
  nodes.push(...renderTokens(marked.lexer(String(markdown).slice(cursor))));
  diagrams.forEach((d, index) => { if (!used.has(index)) nodes.push(diagramNode(d.svg, d.name || d.title || "", t)); });
  return nodes;
}

export function buildIflowDocumentationDefinition(document, { language = document.language || "de" } = {}) {
  const t = translation(language), base = document.base || {};
  const extensions = orderedDocumentationExtensions(document.extensions);
  if (!base.markdown) throw Error(t("empty"));
  const diagrams = [...(base.bpmnSvgs || [])];
  for (const entry of base.bpmnXmls || []) {
    if (!diagrams.some(d => d.name === entry.name)) diagrams.push({ name: entry.name, svg: renderIflowSvgString(entry.xml) });
  }
  const content = [
    { text: t("title"), fontSize: 10, color: PAPER.muted, characterSpacing: 1, margin: [0, 0, 0, 10] },
    { text: document.title || document.flowName || document.flowId, fontSize: 23, bold: true, color: PAPER.ink, margin: [0, 0, 0, 12] },
    { text: document.styleTitle || t(`style.${document.style}`, { defaultValue: document.style || "" }), color: PAPER.muted, margin: [0, 0, 0, 18] },
    { text: t("aiNotice"), fontSize: 9, color: PAPER.muted, margin: [0, 0, 0, 18] },
    ...markdownNodes(base.markdown, [...diagrams, ...(base.svgs || [])], t),
  ];
  if (document.restrictedExtensionsCount) content.push({ text: t("restrictedExtensions", { count: document.restrictedExtensionsCount }), color: PAPER.muted, margin: [0, 8, 0, 8] });
  if (extensions.length) content.push({ text: t("extensions"), style: "h1", pageBreak: "before" });
  extensions.forEach((extension, index) => {
    content.push({ text: t("transport", { number: index + 1 }), style: "h2",
      ...(index ? { pageBreak: "before", pageOrientation: "portrait" } : {}) });
    const facts = documentationExtensionFacts(extension, language);
    if (facts.length) content.push({ text: facts.join("\n"), fontSize: 9, color: PAPER.muted, margin: [0, 0, 0, 10] });
    if (extension.content == null) content.push({ text: t("legacyNotice"), fontSize: 9, color: PAPER.muted, margin: [0, 4, 0, 8] });
    for (const section of documentationSections(extension, language)) {
      content.push({ text: section.title, style: "h3" });
      // Legacy headings stay subordinate to application-owned chapter headings.
      content.push(...markdownNodes(section.markdown, [], t).map(node =>
        node.headlineLevel ? { ...node, style: "h4", headlineLevel: 4 } : node));
    }
    content.push({ text: t("section.comparison"), style: "h3" });
    content.push({ text: t("snapshotNote"), fontSize: 9, color: PAPER.muted, margin: [0, 4, 0, 0] });
    const comparable = Boolean(extension.beforeBpmn && extension.afterBpmn);
    const { marksBefore, marksAfter } = comparable ? documentationDiffMarks(extension.beforeBpmn, extension.afterBpmn) : { marksBefore: {}, marksAfter: {} };
    for (const [label, xml, marks] of [[t("before"), extension.beforeBpmn, marksBefore], [t("after"), extension.afterBpmn, marksAfter]]) {
      content.push({ text: `${t("transport", { number: index + 1 })} · ${label}`, style: "h3", pageBreak: "before" });
      const svg = xml ? renderIflowSvgString(xml, { marks }) : "";
      content.push(svg ? diagramNode(svg, "", t, PAGE_WIDTH, 540) : { text: t("missingDiagram"), color: PAPER.muted });
      content.push({ text: t("legend"), fontSize: 9, color: PAPER.muted });
      content.push({ text: t("snapshotNote"), fontSize: 9, color: PAPER.muted, margin: [0, 4, 0, 0] });
      if (comparable && !Object.keys(marksBefore).length && !Object.keys(marksAfter).length) content.push({ text: t("noDiagramChanges"), fontSize: 9, color: PAPER.muted, margin: [0, 4, 0, 0] });
    }
  });
  return { pageSize: "A4", pageOrientation: "portrait", pageMargins: [60, 50, 60, 50], info: { title: document.title || document.flowId, subject: t("title") }, content,
    defaultStyle: { font: FONT, fontSize: 10.5, lineHeight: 1.3, color: PAPER.text },
    styles: { h1: { fontSize: 19, bold: true, color: PAPER.ink, margin: [0, 18, 0, 10] }, h2: { fontSize: 15, bold: true, color: PAPER.ink, margin: [0, 16, 0, 8] }, h3: { fontSize: 12, bold: true, color: PAPER.ink, margin: [0, 12, 0, 6] }, h4: { fontSize: 11, bold: true, margin: [0, 10, 0, 4] } },
    footer: (page, total) => ({ text: `${page} / ${total}`, alignment: "right", color: PAPER.muted, fontSize: 8, margin: [60, 15, 60, 0] }),
  };
}

export async function generateIflowDocumentationPdf(document, { language = document.language || "de", signal } = {}) {
  const check = () => { if (signal?.aborted) throw new DOMException("Aborted", "AbortError"); };
  check();
  const names = ["Regular", "Semibold", "It", "SemiboldIt"].map(name => `SourceSans3-${name}.ttf`);
  const fonts = { [FONT]: { normal: names[0], bold: names[1], italics: names[2], bolditalics: names[3] } };
  const definition = buildIflowDocumentationDefinition(document, { language });
  const [library, data] = await Promise.all([import("pdfmake/build/pdfmake"), Promise.all(names.map(async name => {
    const response = await fetch(`${process.env.PUBLIC_URL || ""}/fonts/source-sans-3/${name}`, { signal });
    if (!response.ok) throw Error(translation(language)("fontsFailed", { status: response.status }));
    const bytes = new Uint8Array(await response.arrayBuffer()); let binary = "";
    for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
    return [name, btoa(binary)];
  }))]);
  check();
  const pdfMake = library.default || library;
  // Preloaded local fonts allow the synchronous stream path. Its errors are catchable,
  // unlike the callback-only URL resolver; no SVG or Markdown causes remote fetches.
  return new Promise((resolve, reject) => {
    const stream = pdfMake.createPdf(definition, undefined, fonts, Object.fromEntries(data)).getStream();
    const chunks = [];
    const abort = () => { stream.destroy?.(); reject(new DOMException("Aborted", "AbortError")); };
    signal?.addEventListener("abort", abort, { once: true });
    stream.on("data", chunk => chunks.push(chunk));
    stream.on("error", error => { signal?.removeEventListener("abort", abort); reject(error); });
    stream.on("end", () => { signal?.removeEventListener("abort", abort); resolve(new Blob(chunks, { type: "application/pdf" })); });
    stream.end();
  });
}
